import { useId, useState, type FormEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';
import { useMasterList } from '@/features/master-list/use-master-list';
import { useActiveCompany } from '@/lib/active-company-context';

import { relationError } from '../presentation';
import type { RelationType } from '../types';
import { useRelate } from '../use-relations';
import { Select } from '@/components/ui/Select';

// Relaciona el documento con otro de la Lista Maestra (spec §6.2.6). La búsqueda
// corre al enviar; el propio documento no se ofrece y uno ya relacionado se muestra
// sin poder elegirse.

const MIN_TERM = 2;

export function AddRelationModal({
  entryId,
  code,
  excluded,
  onClose,
}: {
  entryId: string;
  code: string;
  /** Documentos ya relacionados (en cualquier dirección). */
  excluded: string[];
  onClose: () => void;
}): ReactElement {
  const searchId = useId();
  const typeId = useId();
  const { activeCompanyId } = useActiveCompany();
  const relate = useRelate(entryId);
  const { showToast } = useToast();
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState<{ id: string; code: string } | null>(null);
  const [type, setType] = useState<RelationType>('relacionado');
  const [error, setError] = useState<string | null>(null);
  const results = useMasterList(
    { limit: 10, page: 1, q: term },
    { companyId: activeCompanyId, enabled: term.length >= MIN_TERM && Boolean(activeCompanyId) },
  );
  const rows = (results.data?.data ?? []).filter((d) => d.id !== entryId);
  const taken = new Set(excluded);

  function search(e: FormEvent): void {
    e.preventDefault();
    const q = text.trim();
    if (q.length >= MIN_TERM) {
      setSelected(null);
      setTerm(q);
    }
  }

  async function confirm(): Promise<void> {
    if (selected === null || relate.isPending) {
      return;
    }
    setError(null);
    try {
      await relate.mutateAsync({ targetEntryId: selected.id, relationType: type });
      showToast(`${code} relacionado con ${selected.code}`);
      onClose();
    } catch (err) {
      setError(relationError(err));
    }
  }

  return (
    <ModalShell
      open
      title="Relacionar documento"
      wide
      busy={relate.isPending}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn mut sm"
            onClick={onClose}
            disabled={relate.isPending}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() => void confirm()}
            disabled={selected === null || relate.isPending}
          >
            {relate.isPending ? 'Relacionando…' : 'Relacionar'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <form onSubmit={search} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div className="fld" style={{ flex: 1 }}>
            <label htmlFor={searchId} className="fld-lab">
              Buscar documento
            </label>
            <input
              id={searchId}
              className="selector fw"
              value={text}
              maxLength={120}
              placeholder="Código o nombre"
              autoComplete="off"
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <button type="submit" className="btn mut sm" disabled={text.trim().length < MIN_TERM}>
            Buscar
          </button>
        </form>
        {term !== '' && results.data !== undefined && rows.length === 0 && (
          <p className="soft" style={{ margin: 0, fontSize: 13 }}>
            No hay documentos que coincidan.
          </p>
        )}
        {results.isError && (
          <div className="fld-error" role="alert">
            No se pudo buscar.
            <button type="button" className="btn mut sm" onClick={() => void results.refetch()}>
              Reintentar
            </button>
          </div>
        )}
        {rows.length > 0 && (
          <fieldset className="fld" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="fld-lab">Resultados</legend>
            <div className="doc-picker" style={{ flexDirection: 'column', flexWrap: 'nowrap' }}>
              {rows.map((d) => (
                <label key={d.id} className="doc-opt">
                  <input
                    type="radio"
                    name="related-document"
                    checked={selected?.id === d.id}
                    disabled={taken.has(d.id)}
                    onChange={() => setSelected({ id: d.id, code: d.code })}
                  />
                  <span className="code">{d.code}</span> {d.title ?? ''}
                  {taken.has(d.id) && <span className="soft">Ya relacionado</span>}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <div className="fld">
          <label htmlFor={typeId} className="fld-lab">
            Relación
          </label>
          <Select
            id={typeId}
            value={type}
            options={[
              { value: 'relacionado', label: 'Este documento lo menciona' },
              { value: 'impacta', label: 'Un cambio aquí lo impacta' },
            ]}
            onChange={(v) => setType(v as RelationType)}
          />
        </div>
        {error && (
          <p className="fld-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
